// Cross-Application UAT / Pilot Acceptance readiness panel.
//
// Lightweight, government-grade visibility into the nine pilot acceptance areas
// reported by the canonical geo-api UAT acceptance endpoints. Rendered only
// for authorized users (the parent gates by role); the service additionally
// returns an `unauthorized` posture if the backend rejects the request.
//
// Presentation aligned to GIISDEC EDS. Readiness calculation, service access,
// authorization posture, normalization and retry behaviour remain unchanged.

import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2,
  Clock,
  ShieldAlert,
  HelpCircle,
  RefreshCw,
  Activity,
} from 'lucide-react';
import {
  uatService,
  normalizeUatAreas,
  uatStateMeta,
  uatAreaFallback,
  type UatReadinessSummary,
  type UatReadinessArea,
} from '@/services/uatService';
import { SmartEmptyState, HelpTip } from '@/components/portal/help/ContextualHelp';
import {
  Badge,
  Button,
  Card,
  Loading,
} from '@/components/eds';

const badgeTone = (
  tone: string,
): 'success' | 'warning' | 'danger' | 'neutral' => {
  if (tone === 'ready') return 'success';
  if (tone === 'partial') return 'warning';
  if (tone === 'blocked') return 'danger';
  return 'neutral';
};

function StateIcon({ tone }: { tone: string }) {
  if (tone === 'ready') {
    return <CheckCircle2 className="h-4 w-4 text-eds-success" aria-hidden="true" />;
  }

  if (tone === 'partial') {
    return <Clock className="h-4 w-4 text-eds-warning" aria-hidden="true" />;
  }

  if (tone === 'blocked') {
    return <ShieldAlert className="h-4 w-4 text-eds-danger" aria-hidden="true" />;
  }

  return <HelpCircle className="h-4 w-4 text-eds-muted-fg" aria-hidden="true" />;
}

const AreaRow: React.FC<{ area: UatReadinessArea }> = ({ area }) => {
  const meta = uatStateMeta(area.state);
  const fb = uatAreaFallback(area);
  const showFallback =
    area.state === 'not_available' || area.state === 'unauthorized';

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <StateIcon tone={meta.tone} />
          <span className="truncate font-eds-body text-sm font-medium text-eds-fg">
            {area.label}
          </span>
          {area.note && (
            <HelpTip text={area.note} topic={`uat:${area.key}`} />
          )}
        </div>

        <Badge tone={badgeTone(meta.tone)} className="shrink-0">
          {meta.label}
        </Badge>
      </div>

      {typeof area.score === 'number' && !showFallback && (
        <div className="mt-3">
          <div className="h-1.5 w-full overflow-hidden rounded-eds-pill bg-eds-surface-2">
            <div
              className={`h-full rounded-eds-pill ${
                meta.tone === 'blocked'
                  ? 'bg-eds-danger'
                  : meta.tone === 'partial'
                    ? 'bg-eds-warning'
                    : 'bg-eds-success'
              }`}
              style={{ width: `${Math.max(0, Math.min(100, area.score))}%` }}
            />
          </div>

          <div className="mt-1 font-eds-body text-[11px] text-eds-muted-fg">
            {Math.round(area.score)}% ready
          </div>
        </div>
      )}

      {!showFallback && area.nextStep && (
        <p className="mt-2 font-eds-body text-xs text-eds-muted-fg">
          <span className="font-medium text-eds-fg">Next: </span>
          {area.nextStep}
        </p>
      )}

      {showFallback && (
        <p className="mt-2 font-eds-body text-xs text-eds-muted-fg">
          {fb.explanation}{' '}
          <span>{fb.nextStep}</span>
        </p>
      )}
    </Card>
  );
};

const UatReadinessPanel: React.FC = () => {
  const [summary, setSummary] = useState<UatReadinessSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await uatService.getReadiness().catch(() => null);
    setSummary(res);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const areas = normalizeUatAreas(summary);
  const overall = summary?.overall ?? 'not_available';
  const overallMeta = uatStateMeta(overall);
  const reported = areas.some(
    (a) => a.state !== 'not_available' && a.state !== 'unauthorized',
  );
  const isUnauthorized = overall === 'unauthorized';

  return (
    <Card className="p-5">
      <div className="mb-1 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-eds-success" aria-hidden="true" />

          <h3 className="font-eds-display text-sm font-semibold text-eds-fg">
            Cross-Application UAT Readiness
          </h3>

          <HelpTip
            topic="uat:overview"
            text="Pilot acceptance posture across investor onboarding, KYC, the LOI → NDA → Payment → MOU workflow, documents, GIS access, and portal ↔ API connectivity."
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          leftIcon={
            <RefreshCw
              className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
          }
        >
          Refresh
        </Button>
      </div>

      {!isUnauthorized && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Badge tone={badgeTone(overallMeta.tone)}>
            Overall: {overallMeta.label}
          </Badge>

          {summary && summary.overallScore > 0 && (
            <span className="font-mono text-xs text-eds-muted-fg">
              {summary.overallScore}% aggregate
            </span>
          )}

          {summary?.generatedAt && (
            <span className="font-eds-body text-[11px] text-eds-muted-fg">
              Updated {new Date(summary.generatedAt).toLocaleString()}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <Loading
          label="Loading UAT readiness…"
          className="py-8"
        />
      ) : isUnauthorized ? (
        <SmartEmptyState
          icon={ShieldAlert}
          title="UAT readiness is restricted"
          explanation="Pilot acceptance readiness is only visible to authorized GIISDEC administrators."
          nextStep="Sign in with an administrator account, or contact the GIISDEC platform team for access."
        />
      ) : !reported ? (
        <SmartEmptyState
          icon={Activity}
          title="No UAT results reported yet"
          explanation="geo-api has not published pilot acceptance readiness for these areas yet."
          nextStep="Run the UAT checks in geo-api (investor onboarding, KYC, LOI/NDA/Payment/MOU, documents, GIS, portal↔API), then refresh."
          actionLabel="Refresh now"
          onAction={load}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((a) => (
            <AreaRow key={a.key} area={a} />
          ))}
        </div>
      )}

      {summary?.requestId && (
        <p className="mt-4 font-mono text-[11px] text-eds-muted-fg">
          requestId: {summary.requestId}
        </p>
      )}
    </Card>
  );
};

export default UatReadinessPanel;
